import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import HeroAutoplay from "@/components/HeroAutoplay";
import Top10Rail from "@/components/Top10Rail";
import ContentRow from "@/components/ContentRow";
import { NotFound, Forbidden } from "@/pages/ErrorPages";
import { Play, Plus, Info } from "lucide-react";

export default function SubscriberHome() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const { user, status, logout } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState([]);
  const [pageStatus, setPageStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    setPageStatus("loading");
    api.get(`/public/tenants/${slug}/landing`)
      .then((r) => alive && (setData(r.data), setPageStatus("ready")))
      .catch(() => alive && setPageStatus("not_found"));
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (status !== "authenticated") return;
    api.get("/me/progress")
      .then((r) => setProgress(r.data))
      .catch(() => {});
  }, [status]);

  const tenant = data?.tenant;
  const isAuthed = status === "authenticated" && !!user;

  // Build hero list — top 5 featured-with-trailer items, fallback to top10 first 5
  const heroItems = useMemo(() => {
    if (!data) return [];
    const seen = new Set();
    const out = [];
    if (data.hero) { out.push(data.hero); seen.add(data.hero.id); }
    for (const c of (data.top10_week || [])) {
      if (out.length >= 5) break;
      if (seen.has(c.id)) continue;
      out.push(c);
      seen.add(c.id);
    }
    for (const c of (data.new_releases || [])) {
      if (out.length >= 5) break;
      if (seen.has(c.id)) continue;
      out.push(c);
      seen.add(c.id);
    }
    return out;
  }, [data]);

  if (status === "loading" || pageStatus === "loading") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] grid place-items-center text-white/40 text-sm uppercase tracking-widest" data-testid="subscriber-home-loading">
        Loading…
      </div>
    );
  }
  if (pageStatus === "not_found" || !data) return <NotFound />;
  if (isAuthed && user.role === "subscriber" && user.tenant_id && tenant?.id !== user.tenant_id) {
    return <Forbidden />;
  }

  const themeColor = tenant.theme_color;
  const accentColor = tenant.accent_color;

  // Continue watching (in-progress, not completed, this tenant only)
  const continueWatching = isAuthed
    ? progress
        .filter((p) => p.content && p.content.tenant_id === tenant.id && !p.completed && p.position_seconds > 5)
        .slice(0, 12)
        .map((p) => ({
          ...p.content,
          _progress_pct: p.duration_seconds > 0 ? (p.position_seconds / p.duration_seconds) * 100 : 0,
        }))
    : [];

  return (
    <StorefrontLayout
      tenant={tenant}
      currentUser={isAuthed ? user : null}
      onLogout={logout}
      showSubscriberNav={isAuthed}
      heroBleed
    >
      <HeroAutoplay
        items={heroItems}
        themeColor={themeColor}
        accentColor={accentColor}
        heightClass="h-[60vh] md:h-[72vh] lg:h-[80vh] min-h-[420px]"
        testid="subscriber-hero"
      >
        {(active) => (
          <>
            <button
              onClick={() => navigate(sp(`/watch/${active.slug}`))}
              className="inline-flex items-center gap-2 px-6 md:px-8 h-12 md:h-14 text-sm md:text-base font-bold uppercase tracking-[0.2em] bg-white text-black hover:bg-white/90 transition-colors"
              data-testid="subscriber-hero-play"
            >
              <Play className="w-4 h-4 fill-black" /> Play
            </button>
            <button
              onClick={async () => {
                try { await api.post(`/me/watchlist`, { content_id: active.id }); } catch (e) {/* ignore */}
              }}
              className="inline-flex items-center gap-2 px-5 md:px-7 h-12 md:h-14 text-sm md:text-base font-bold uppercase tracking-[0.2em] border border-white/40 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white transition-colors"
              data-testid="subscriber-hero-add"
            >
              <Plus className="w-4 h-4" /> My list
            </button>
            <Link
              to={sp(`/title/${active.slug}`)}
              className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.25em] text-white/75 hover:text-white px-3 md:px-4 h-12 md:h-14"
              data-testid="subscriber-hero-info"
            >
              <Info className="w-4 h-4" /> More info
            </Link>
          </>
        )}
      </HeroAutoplay>

      <div className="max-w-[1500px] mx-auto pt-2 pb-16 space-y-4" data-testid="subscriber-home-rows">
        {continueWatching.length > 0 && (
          <ContentRow
            name="Continue watching"
            items={continueWatching}
            tenantSlug={slug}
            themeColor={themeColor}
            kind="mixed"
          />
        )}

        {data.top10_week && data.top10_week.length > 0 && (
          <Top10Rail
            title={`Top 10 on ${tenant.name} today`}
            items={data.top10_week}
            tenantSlug={slug}
            themeColor={themeColor}
            testid="subscriber-top10-rail"
          />
        )}

        {data.reels_preview && data.reels_preview.length > 0 && (
          <section className="px-6 md:px-10 py-4 group/row" data-testid="subscriber-reels-preview">
            <div className="flex items-end justify-between mb-3">
              <h2 className="font-display font-bold text-lg md:text-xl tracking-tight">Reels — short and sweet</h2>
              <Link
                to={sp(`/reels`)}
                className="text-[11px] uppercase tracking-[0.3em] hover:underline"
                style={{ color: accentColor }}
                data-testid="subscriber-reels-see-all"
              >
                See all →
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
              {data.reels_preview.slice(0, 8).map((r) => (
                <Link
                  key={r.id}
                  to={sp(`/reels?start=${r.slug}`)}
                  className="snap-start flex-shrink-0 w-[140px] md:w-[160px] aspect-[9/16] relative bg-[#121212] border border-white/10 hover:border-white/40 overflow-hidden group transition-all duration-200 hover:scale-[1.04]"
                  data-testid={`subscriber-reel-${r.slug}`}
                >
                  {r.thumbnail_url && (
                    <img src={r.thumbnail_url} alt={r.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                  <div className="absolute bottom-2 inset-x-2 text-xs font-bold leading-tight line-clamp-2">{r.title}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {data.trending_movies && data.trending_movies.length > 0 && (
          <ContentRow
            name="Trending movies"
            items={data.trending_movies}
            tenantSlug={slug}
            themeColor={themeColor}
            kind="movie"
          />
        )}

        {data.new_releases && data.new_releases.length > 0 && (
          <ContentRow
            name="Recently added"
            items={data.new_releases}
            tenantSlug={slug}
            themeColor={themeColor}
            kind="mixed"
          />
        )}

        {data.genres.map((g) => (
          <ContentRow
            key={g.name}
            name={g.name}
            items={g.items}
            tenantSlug={slug}
            themeColor={themeColor}
            kind="genre"
          />
        ))}
      </div>
    </StorefrontLayout>
  );
}
